import { Link } from 'react-router-dom';

import { ESTADOS } from '../api/vencimientosApi.js';
import {
  claseDeEstado, fecha as formatearFecha, iconoDeEstado, importe as formatearEnDivisa,
  inicialesDeTarjeta, nombreDeTarjeta,
} from '../formato.js';
import { BloqueDeCorteEnCurso } from './BloqueDeCorteEnCurso.jsx';
import { CifrasQueSiSeSaben } from '../../deuda-descompuesta/componentes/CifrasQueSiSeSaben.jsx';
import { ContadorDeMensualidad } from '../../deuda-descompuesta/componentes/ContadorDeMensualidad.jsx';
import { DesgloseDelPagoEsperado } from '../../deuda-descompuesta/componentes/DesgloseDelPagoEsperado.jsx';

/**
 * UN RENGLÓN DEL PANEL: dos bloques que hablan de la misma tarjeta y de dinero
 * distinto.
 *
 *   BLOQUE 1 · LO EXIGIBLE — ya cortó, tiene fecha límite y tiene estado. Es lo que
 *              se paga y lo único que suma al total del panel.
 *   BLOQUE 2 · CORTE EN CURSO — todavía no cortó, no tiene estado y hoy no se paga.
 *
 * ESTA PANTALLA NO DECIDE NADA (RNF-059): no compara fechas, no resta importes, no
 * deduce qué está vencido, no compone el texto del estado y no concatena el
 * contador de mensualidades. Recibe todo armado y lo pinta.
 */
export function RenglonDeVencimiento({ tarjeta, rutaDeDetalle }) {
  /*
   * [NUEVO · incremento 20/21 · RN-750, D-441, P-20-04] EL RENGLÓN DEL PRÉSTAMO.
   *
   * LA SEÑAL ES EL CAMPO (patrón D-427): se pregunta por `esDePrestamo`, que el
   * servidor manda, y NO se deduce de que falten los cuatro dígitos, ni del nombre,
   * ni de `esCredito` —que desde el tipo Préstamo solo dice «es pasivo» (RN-752)—.
   *
   * ES LA MISMA CAJA que la de una tarjeta, con sus iniciales, su insignia y su
   * cifra grande; lo que cambia es QUÉ SE DICE, porque un préstamo no tiene corte,
   * ni días de gracia, ni compras a meses, ni diferido declarado, y no ofrece
   * liquidar antes, aplicar devolución ni descontar lo declarado.
   */
  if (tarjeta.esDePrestamo) {
    return <RenglonDelPrestamo prestamo={tarjeta} />;
  }

  const clase = claseDeEstado(tarjeta.claveEstado);
  const sinEstado = tarjeta.claveEstado === null || tarjeta.claveEstado === undefined;
  const sePuedePagar = tarjeta.claveEstado === ESTADOS.vencida || tarjeta.claveEstado === ESTADOS.porVencer;
  // Incremento 18/21: con la tarjeta pagada o sin saldo, la cifra del periodo ya
  // es historia y el rótulo se dice en pasado (prototipo v39).
  const yaNoSeEspera = tarjeta.claveEstado === ESTADOS.pagada || tarjeta.claveEstado === ESTADOS.sinSaldo;
  // Toda la deuda al corte son compras a meses: el periodo no tiene nada que
  // cobrar, y eso se dice con palabras en vez de dejar un $0.00 sin explicar
  // (RN-693, CA-18-08).
  const todoAMeses = tarjeta.claveEstado === ESTADOS.sinSaldo
    && Number(tarjeta.importeSaldoAlCorte ?? 0) > 0;

  return (
    <article className={`card tarjeta-vencimiento${tarjeta.avisoActivo ? ` resaltada ${clase}` : ''}`}
      data-prueba="renglon-de-vencimiento" data-cuenta={tarjeta.idCuenta}>

      {/* El resalte lo decide el servidor con los días configurados (RN-259). El
          número gobierna el RESALTE, nunca la lista ni los importes (RN-260). */}
      {tarjeta.avisoActivo && (
        <div className={`banda ${clase}`} data-prueba="banda-de-aviso">
          <span aria-hidden="true">{iconoDeEstado(tarjeta.claveEstado)}</span> Aviso · {tarjeta.textoEstado}
        </div>
      )}

      <div className="fila">
        <span className="avat" aria-hidden="true">{inicialesDeTarjeta(tarjeta.nombreTarjeta)}</span>
        <span className="crece">
          <b data-prueba="nombre-de-tarjeta">{nombreDeTarjeta(tarjeta)}</b>
          {/* Las dos personas ven TODAS las tarjetas, con el nombre de su dueño
              (RN-027, RN-274). */}
          <small>de {tarjeta.nombrePropietario}</small>
        </span>
        <span className={`chip ${clase}`} data-prueba="estado-de-tarjeta">
          <span aria-hidden="true">{iconoDeEstado(tarjeta.claveEstado)}</span>{' '}
          {tarjeta.nombreEstado ?? 'Sin estado'}
        </span>
      </div>

      {/* --- BLOQUE 1 · LO EXIGIBLE ------------------------------------------ */}
      {sinEstado ? (
        <div className="aviso e" role="status" data-prueba="renglon-sin-estado">
          ⚠ {tarjeta.advertencia}
          <span className="ayuda">
            No se asume cero, no se asume pagada y no se asume vencida. Esta tarjeta no suma al total
            y el total se muestra con advertencia.
          </span>
          {/* [NUEVO · incremento 18/21 · RN-702, D-396] Si lo único que no se pudo
              comprobar es el reparto en compras a meses, el saldo al corte y lo
              declarado SÍ se saben y se enseñan: es lo que hace verdadera la
              última frase del aviso. Si lo que falló fue el saldo o los pagos, no
              llega ninguna de las dos y esto no pinta nada. */}
          <CifrasQueSiSeSaben tarjeta={tarjeta} />
        </div>
      ) : (
        <div className={`exigible ${clase}`} data-prueba="bloque-exigible">
          {/* [CAMBIADO · incremento 18/21 · RF-250, D-380] El rótulo nombra la
              cifra nueva: lo que se espera pagar ESTE PERIODO, no la deuda
              acumulada. En pasado cuando ya no hay nada que hacer con ella. */}
          <div className="exigible-rot">
            {yaNoSeEspera ? 'Se esperaba pagar este periodo' : 'Se espera pagar este periodo'} · periodo
            que cortó el {formatearFecha(tarjeta.fechaCorte)}
          </div>
          <div className="exigible-imp" data-prueba="importe-pendiente">
            {formatearEnDivisa(tarjeta.importePendiente, tarjeta.codigoISO)}
          </div>
          {/* Texto YA ARMADO: "Vence en 3 días", "Venció hace 2 días", "Pagada el
              24/08/2026". La pantalla NO lo compone (RNF-059). */}
          <div className="exigible-txt" data-prueba="texto-de-estado">
            <span aria-hidden="true">{iconoDeEstado(tarjeta.claveEstado)}</span> {tarjeta.textoEstado}
          </div>

          {tarjeta.claveEstado === ESTADOS.pagada && (
            <span className="ayuda" data-prueba="detalle-pagada">
              {/* [CAMBIADO · incremento 18/21 · RN-693] Se compara contra el PAGO
                  ESPERADO, que es lo que el banco cobraba de este periodo: antes
                  se decía «de la deuda al corte» y por eso pagar el corte completo
                  parecía insuficiente. */}
              Se cubrieron {formatearEnDivisa(tarjeta.importeCubierto, tarjeta.codigoISO)} de{' '}
              {formatearEnDivisa(tarjeta.importePagoEsperado, tarjeta.codigoISO)} con{' '}
              {tarjeta.cantidadDePagosDelPeriodo}
              {tarjeta.cantidadDePagosDelPeriodo === 1 ? ' pago' : ' pagos'}.
              {tarjeta.pagadaConAtraso && ' Se pagó después de la fecha límite y aun así queda pagada:'
                + ' no existe un estado «vencida y pagada».'}
              {' '}Nadie la marcó: se dedujo de los pagos registrados.
            </span>
          )}

          {tarjeta.claveEstado === ESTADOS.sinSaldo && (
            <span className="ayuda" data-prueba="detalle-sin-saldo">
              {tarjeta.sinMovimientos && 'Aún no tienes movimientos en esta tarjeta. '}
              {/* [NUEVO · incremento 18/21 · RN-693, CA-18-08] Caso real de la
                  titular: la tarjeta debe al corte, pero todo son compras a meses.
                  Sin esta frase el renglón enseñaría $0.00 sin decir por qué. */}
              {todoAMeses && 'Toda la deuda de esta tarjeta son compras a meses que el banco todavía '
                + 'no te cobra, así que este periodo no hay nada que pagar. '}
              No hay nada que cobrar en este periodo, así que no está «pagada» —nadie pagó nada— ni
              está «por vencer» —no hay qué pagar—. No lleva aviso y no suma al total.
            </span>
          )}

          {(tarjeta.claveEstado === ESTADOS.porVencer || tarjeta.claveEstado === ESTADOS.vencida) && (
            <span className="ayuda">
              Fecha límite <b>{formatearFecha(tarjeta.fechaLimitePago)}</b> · corte{' '}
              {formatearFecha(tarjeta.fechaCorte)} · {tarjeta.diasDeGracia} días de gracia.
            </span>
          )}

          {/* Pago PARCIAL: el estado NO cambia; lo que importa es cuánto falta
              (RN-251). No existe «pagada parcialmente». */}
          {tarjeta.hayPagoParcial && (
            <div className="parcial" data-prueba="pago-parcial">
              <b>
                {/* [CAMBIADO · incremento 18/21 · RN-695] Parcial es contra el
                    PAGO ESPERADO: 0 < cubierto < pago esperado. */}
                Abonaste {formatearEnDivisa(tarjeta.importeCubierto, tarjeta.codigoISO)} de{' '}
                {formatearEnDivisa(tarjeta.importePagoEsperado, tarjeta.codigoISO)}
              </b>
              <span className="ayuda">
                Te faltan <b>{formatearEnDivisa(tarjeta.importePendiente, tarjeta.codigoISO)}</b> ·{' '}
                {tarjeta.cantidadDePagosDelPeriodo}
                {tarjeta.cantidadDePagosDelPeriodo === 1 ? ' pago' : ' pagos'} en la ventana del
                periodo. Un pago parcial no cambia el estado.
              </span>
            </div>
          )}

          {/* ===== DE DÓNDE SALE LA CIFRA · incremento 18/21 (RF-252) ==========
              Va DESPUÉS de la cifra protagonista y ANTES del desglose de meses:
              sin él, nadie podría ver por qué el periodo ya no pide toda la deuda
              al corte. Las cuatro cifras llegan calculadas del servidor. */}
          <DesgloseDelPagoEsperado tarjeta={tarjeta} enPasado={yaNoSeEspera} />

          {/* RN-706 / D-391 · El diferido declarado quedó por encima de la deuda
              porque la deuda bajó después. No se ajustó nada y no hay marca en
              ninguna parte: el aviso se deriva al consultar. */}
          {tarjeta.textoAvisoDeDiferidoFueraDeRango && (
            <div className="aviso w" role="status" data-prueba="aviso-diferido-fuera-de-rango">
              {tarjeta.textoAvisoDeDiferidoFueraDeRango}
            </div>
          )}

          {/* RN-700 / D-383 · Lo declarado todavía no tiene planes detrás, o se
              traslapa con los que ya hay. Se REPORTA, nunca se ajusta. */}
          {tarjeta.textoAvisoDeDiferidoDeclarado && (
            <div className="aviso i" role="status" data-prueba="aviso-diferido-declarado">
              {tarjeta.textoAvisoDeDiferidoDeclarado}
            </div>
          )}

          {/* Mensualidades del periodo: DESGLOSE de lo que ya está contado. No se
              suman ni se restan de lo exigible (RF-118, RN-268). */}
          {tarjeta.mensualidadesDelPeriodo?.length > 0 && (
            <div className="msi" data-prueba="mensualidades-del-periodo">
              <div className="msi-t">Mensualidades de compras a meses en este periodo</div>
              <ul>
                {tarjeta.mensualidadesDelPeriodo.map((m) => (
                  <li key={m.idMensualidad}>
                    {m.descripcionDelGasto}{' '}
                    {/* RNF-132 / RN-707 · PIEZA COMPARTIDA del contador: número
                        legible y distinción histórica por medios que no son el
                        color. El texto «3/12» llega armado (RNF-038). */}
                    <ContadorDeMensualidad contador={m.contadorDeMensualidad} />{' '}
                    {formatearEnDivisa(m.importeImputadoAlMes, tarjeta.codigoISO)}
                  </li>
                ))}
              </ul>
              <span className="ayuda">{tarjeta.textoDesgloseDeMeses}</span>
            </div>
          )}

          {/* [RE-REDACTADO Y MOVIDO · incremento 18/21 · RN-704, D-386] Cambia la
              redacción, no el fondo: lo que sigue siendo mayor que el estado de
              cuenta es la DEUDA TOTAL; la cifra a pagar ya corresponde al cobro
              del banco. D-53 queda intacta.
              SALE DEL BLOQUE DE MENSUALIDADES porque ahora también aplica cuando
              el diferido es declarado y no hay ninguna mensualidad en el periodo
              (RN-704): dentro de ese bloque se habría quedado invisible justo en
              el caso real de la titular. La condición la sigue decidiendo el
              servidor y solo se amplía, nunca se recorta.

              [CORREGIDO · vuelta de correccion de la RONDA[1/2], hallazgo MAYOR]
              EL TEXTO LO ARMA EL SERVIDOR (RNF-130, RNF-059), igual que el
              desglose y los dos avisos del diferido. Estaba escrito a mano aquí y
              en el detalle del vencimiento: el mismo párrafo duplicado en dos
              pantallas. La condición no cambia —el campo viaja no nulo exactamente
              cuando `avisoDivergenciaConBanco` es verdadero (contratos §1,
              aditivo)—, así que lo que se ve es lo mismo. */}
          {tarjeta.textoAvisoDeDivergenciaConBanco && (
            <div className="aviso w" role="status" data-prueba="aviso-divergencia">
              {tarjeta.textoAvisoDeDivergenciaConBanco}
            </div>
          )}
        </div>
      )}

      {/* --- Acciones -------------------------------------------------------- */}
      <div className="fila acciones">
        {sePuedePagar && (
          // EL 6/6 NO CREA MOVIMIENTOS (RF-121): lleva al traspaso del 3/6 con la
          // tarjeta como destino y el importe pendiente sugerido, y la persona elige
          // de qué cuenta suya sale el dinero (RN-067).
          //
          // [NUEVO · incremento 19/21 · PUNTO 2 · D-414 («opcion A»), RF-262,
          // RN-726] `importeSugerido` es EL DATO DE LA APERTURA: la pantalla de
          // traspaso lo usa como valor inicial SOLO cuando llega. Va con nombre
          // propio y no se reaprovecha `importe`, que sigue sin leerse: así la
          // apertura desde el panel es la ÚNICA descongelada y el alta genérica y
          // los enlaces de Meses sin intereses siguen abriendo vacíos (RN-427,
          // D-229 y D-241 vigentes para todo lo demás).
          <Link
            className="btn"
            data-prueba="registrar-pago"
            to={`/movimientos/traspaso?idCuentaDestino=${tarjeta.idCuenta}`
              + `&importe=${tarjeta.importePendiente}`
              + `&importeSugerido=${tarjeta.importePendiente}`}
          >
            Registrar pago
          </Link>
        )}
        <Link className="btn sec" to={rutaDeDetalle} data-prueba="ver-detalle">Ver detalle</Link>
        <Link className="btn sec" to={`/cuentas/${tarjeta.idCuenta}`}>Ver tarjeta</Link>
      </div>

      {/* --- BLOQUE 2 · CORTE EN CURSO --------------------------------------- */}
      <BloqueDeCorteEnCurso corte={tarjeta.corteEnCurso} codigoISO={tarjeta.codigoISO} />
    </article>
  );
}

/**
 * EL PAGO DEL PRÉSTAMO EN EL PANEL (P-20-04, RF-266, RF-269, RN-749, RN-750).
 *
 * ESTA PIEZA NO DECIDE NADA Y NO CALCULA NADA (RNF-059, RNF-038): el pago del mes,
 * lo ya pagado, lo que falta, la etiqueta, el contador «15/48» y el párrafo de «cómo
 * sale esta cifra» llegan ARMADOS del servidor. Aquí no se resta, no se compara
 * ninguna fecha y no se concatena ningún contador.
 *
 * LA CIFRA GRANDE ES LO QUE FALTA DEL PAGO DE ESTE MES, NUNCA LA DEUDA, y el renglón
 * lo dice con palabras: es la confusión que esta cifra invita a hacer (D-438).
 *
 * EL CONTADOR DE AQUÍ ES EL DEL PAGO QUE TOCA ESTE MES —15/48— y vale lo mismo antes
 * y después de registrarlo: lo fija el periodo, no el pago (RN-779, D-458). El otro
 * número, el avance del plan (14/48), vive en el detalle del préstamo y responde otra
 * pregunta; los dos conviven sin contradecirse (CA-20-31).
 */
function RenglonDelPrestamo({ prestamo }) {
  const clase = claseDeEstado(prestamo.claveEstado);
  const puedePagar = Number(prestamo.importePendienteDelEsperado ?? 0) > 0;

  return (
    <article
      className={`card tarjeta-vencimiento${prestamo.avisoActivo ? ` resaltada ${clase}` : ''}`}
      data-prueba="renglon-de-vencimiento"
      data-cuenta={prestamo.idCuenta}
      data-prestamo="si"
    >
      {prestamo.avisoActivo && (
        <div className={`banda ${clase}`} data-prueba="banda-de-aviso">
          <span aria-hidden="true">{iconoDeEstado(prestamo.claveEstado)}</span> Aviso · {prestamo.textoEstado}
        </div>
      )}

      <div className="fila">
        <span className="avat" aria-hidden="true">{inicialesDeTarjeta(prestamo.nombreTarjeta)}</span>
        <span className="crece">
          {/* «Pago del préstamo NOMBRE», armado por el servidor (D-441, RNF-130). */}
          <b data-prueba="nombre-de-tarjeta">
            {prestamo.textoIdentificacionDelPrestamo ?? prestamo.nombreTarjeta}
          </b>
          <small>
            de {prestamo.nombrePropietario}
            {prestamo.contadorDeMensualidadDelPeriodo && (
              <>
                {' · pago '}
                {/* RN-779 (b) · la IDENTIDAD de la mensualidad del periodo. */}
                <b data-prueba="contador-del-prestamo">{prestamo.contadorDeMensualidadDelPeriodo}</b>
              </>
            )}
            {' · día '}
            {formatearFecha(prestamo.fechaLimitePago)}
          </small>
        </span>
        {/* La insignia lleva la etiqueta del PERIODO del préstamo, que tiene cuatro
            valores —incluido «pagado en parte»—, mientras el estado del panel sigue
            siendo el de siempre para el orden y los totales (RN-749). */}
        <span className={`chip ${clase}`} data-prueba="estado-de-tarjeta">
          <span aria-hidden="true">{iconoDeEstado(prestamo.claveEstado)}</span>{' '}
          {prestamo.textoEtiquetaDelPeriodo ?? prestamo.nombreEstado ?? 'Sin estado'}
        </span>
      </div>

      <div className={`exigible ${clase}`} data-prueba="bloque-exigible">
        <div className="exigible-rot">
          Falta del pago de este mes · se paga el {formatearFecha(prestamo.fechaLimitePago)}
        </div>
        <div className="exigible-imp" data-prueba="importe-pendiente">
          {formatearEnDivisa(prestamo.importePendienteDelEsperado, prestamo.codigoISO)}
        </div>
        <div className="exigible-txt" data-prueba="texto-de-estado">
          <span aria-hidden="true">{iconoDeEstado(prestamo.claveEstado)}</span> {prestamo.textoEstado}
        </div>

        {/* «CÓMO SALE ESTA CIFRA» · el párrafo lo arma el servidor y dice
            expresamente que esto no es la deuda (RN-750, RNF-130). */}
        <span className="ayuda" data-prueba="desglose-del-prestamo">
          {prestamo.textoDesgloseDelEsperado}
        </span>

        {prestamo.hayPagoParcial && (
          <div className="parcial" data-prueba="pago-parcial">
            <b>
              Abonaste {formatearEnDivisa(prestamo.importeCubierto, prestamo.codigoISO)} de{' '}
              {formatearEnDivisa(prestamo.importeExigible, prestamo.codigoISO)}
            </b>
            <span className="ayuda">
              Te faltan <b>{formatearEnDivisa(prestamo.importePendienteDelEsperado, prestamo.codigoISO)}</b>{' '}
              del pago de este mes. Nadie marcó nada: se deduce de tus pagos registrados.
            </span>
          </div>
        )}
      </div>

      <div className="fila acciones">
        {puedePagar && (
          /*
           * EL PENDIENTE VIAJA COMO DATO DE LA APERTURA (D-438, D-414/RN-726): la
           * pantalla del pago lo usa como valor inicial de «Lo que pagaste», editable
           * (RNF-133). Abierta por otro camino, manda la precarga del servidor.
           */
          <Link
            className="btn"
            data-prueba="registrar-pago"
            to={`/cuentas/prestamos/${prestamo.idCuenta}/pagos/nuevo`
              + `?desde=vencen&pendiente=${prestamo.importePendienteDelEsperado}`}
          >
            Registrar pago
          </Link>
        )}
        <Link
          className="btn sec"
          to={`/cuentas/prestamos/${prestamo.idCuenta}`}
          data-prueba="ver-detalle"
        >
          Ver el préstamo
        </Link>
        <Link className="btn sec" to={`/movimientos?idCuenta=${prestamo.idCuenta}`}>
          Ver sus movimientos
        </Link>
      </div>
    </article>
  );
}
